import { Link, useNavigate } from 'react-router';
import { PageHead } from 'ui/kit/PageHead';
import { Bell, BellDot } from 'lucide-react';

import { getNotificationPath } from 'components/NotificationBell/NotificationBell';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  type UserNotification,
} from 'hooks/useNotifications';

import classes from './t3Screen.module.scss';
import { BrandLoader } from 'ui/BrandLoader/BrandLoader';

const formatDate = (value: string) => {
  try {
    return new Date(value).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
};

const actionLabel = (item: UserNotification) => {
  const type = item.notification_type;
  if (type.startsWith('moderation_')) return 'Открыть профиль';
  if (type.startsWith('chat') || type === 'student_invitation') return 'Открыть чаты';
  if (type === 'passport_photo_rejected' || type === 'passport_expired') return 'Открыть документы';
  return 'Открыть';
};

export const TeacherNotificationsPage = () => {
  const navigate = useNavigate();
  const { data, isLoading } = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const items = data?.items ?? [];
  const unread = data?.unread_count ?? 0;

  const openItem = (item: UserNotification) => {
    if (!item.is_read) markRead.mutate(item.id);
    const path = getNotificationPath(item, 'teacher');
    if (path) navigate(path);
  };

  return (
    <main className={classes.page}>
      <PageHead crumb={<>Личный кабинет / Уведомления</>} title={<>Уведомления</>} />
      <section className={classes.card}>
        <h2 className={classes.cardTitle}>Список уведомлений</h2>
        <p className={classes.muted}>Сообщения о профиле, чатах и проверке документов.</p>
        {isLoading ? <BrandLoader center label='Загрузка...' /> : null}
        {!isLoading && items.length === 0 ? (
          <p className={classes.body}>Пока нет уведомлений. Когда появится событие, оно будет здесь.</p>
        ) : null}
        <ul className={classes.list}>
          {items.map((item) => {
            const path = getNotificationPath(item, 'teacher');
            return (
              <li
                key={item.id}
                className={item.is_read ? classes.item : `${classes.item} ${classes.itemUnread}`}
              >
                <span className={classes.itemIcon} aria-hidden>
                  {item.is_read ? <Bell size={20} /> : <BellDot size={20} />}
                </span>
                <div className={classes.itemText}>
                  <p className={classes.itemTitle}>{item.title}</p>
                  <p className={classes.body}>{item.body}</p>
                  <p className={classes.small}>
                    {item.is_read ? 'Прочитано' : 'Не прочитано'}
                    {formatDate(item.created_at) ? ` · ${formatDate(item.created_at)}` : ''}
                  </p>
                </div>
                {path ? (
                  <button type='button' className={classes.itemAction} onClick={() => openItem(item)}>
                    {actionLabel(item)}
                  </button>
                ) : !item.is_read ? (
                  <button
                    type='button'
                    className={classes.itemAction}
                    onClick={() => markRead.mutate(item.id)}
                  >
                    Отметить прочитанным
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
        {unread > 0 ? (
          <button
            type='button'
            className={`${classes.secondary} ${classes.secondaryFit}`}
            onClick={() => markAllRead.mutate()}
            disabled={markAllRead.isPending}
          >
            Отметить все прочитанными
          </button>
        ) : null}
      </section>
      <section className={classes.card}>
        <h2 className={classes.cardTitle}>Настройки уведомлений</h2>
        <p className={classes.body}>
          Способ получения уведомлений можно проверить в настройках аккаунта.
        </p>
        <Link to='/teacher/settings' className={`${classes.secondary} ${classes.secondaryFit}`}>
          Открыть настройки
        </Link>
      </section>
    </main>
  );
};
